import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorker } from "../context/WorkerContext";
import { formatTimePart } from "../helpers/change_payout_date_helper";
import { MODES, formatDate } from "./paymentSettingUtils";

const WorkerPaymentSetting = ({ onClose }) => {
  const navigate = useNavigate();
  const { user, worker_payout_frequency_history } = useWorker();

  const [selected, setSelected] = useState(user?.payoutFrequency ?? null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (user?.payoutFrequency) {
      setSelected(user.payoutFrequency);
    }
  }, [user?.payoutFrequency]);

  const loadHistory = useCallback(
    async (id) => {
      if (!id) return;
      setLoadingHistory(true);
      try {
        const res = await worker_payout_frequency_history(id);
        setSelected(res?.data?.payoutFrequency ?? null);
        setHistory(res?.data?.history ? [...res.data.history] : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingHistory(false);
      }
    },
    []
  );

  useEffect(() => {
    if (user?.id) {
      loadHistory(user.id);
    }
  }, [user?.id, loadHistory]);

  function handleClose() {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f0f5",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        paddingTop: "90px",
      }}
    >
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <div className="relative flex items-center justify-center">
          <button onClick={handleClose} className="text-2xl absolute left-6">
            ‹
          </button>
          <h2 className="text-xl font-bold text-center">Payout Settings</h2>
        </div>
        <div className="relative flex items-center justify-center mb-6">
          <p className="text-sm text-gray-500">Choose how you want to receive your earning</p>
        </div>

        <div className="px-5 pb-10">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 mt-4">Preferred Payout mode</h3>

          <p className="text-xs text-gray-400 mb-3">
            Only an admin can change this setting. Please contact your admin
          </p>

          <div className="space-y-1.5">
            {MODES.map(({ id, title, desc, icon }) => {
              const active = selected === id;
              return (
                <div
                  key={id}
                  className={`w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-colors opacity-60 cursor-not-allowed ${
                    active ? "border-violet-500 bg-[#FBF9FD]" : "border-gray-200 shadow-gray-400 bg-[#FBF9FD]"
                  }`}
                >
                  <span
                    className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-base ${
                      active ? "bg-violet-300 text-white" : "bg-gray-100"
                    }`}
                  >
                    <img src={icon} alt="" />
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-bold text-gray-900">{title}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{desc}</span>
                  </span>
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      active ? "border-violet-600" : "border-gray-300"
                    }`}
                  >
                    {active && <span className="w-2.5 h-2.5 rounded-full bg-violet-600" />}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="pb-2">
            <h3 className="text-sm font-semibold text-gray-700 mt-3">Payout Mode History</h3>
          </div>

          {loadingHistory ? (
            <div className="text-sm text-gray-400 py-6 text-center">Loading history...</div>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No changes yet.</p>
          ) : (
            <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
              {history.map((h) => (
                <div key={h._id} className="rounded-xl border border-gray-100 bg-white shadow-sm px-4 py-3">
                  <div className="flex justify-between items-center text-xs text-gray-400">
                    <span>
                      On {formatDate(h.doneAt)} {formatTimePart(h.doneAt)}
                    </span>
                    {h.appliedFrom && <span>Applied from: {formatDate(h.appliedFrom)}</span>}
                  </div>
                  <p className="text-sm text-gray-700 mt-1">
                    The payout mode was changed from{" "}
                    <span className="font-semibold text-gray-900 capitalize">{h.oldValue}</span> to{" "}
                    <span className="font-semibold text-gray-900 capitalize">{h.newValue}.</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerPaymentSetting;